
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { TrendingUp, TrendingDown, Target, Award, AlertTriangle } from 'lucide-react';
import { QueryType } from './QueryUnderstanding';

import type {
  ChartConfig,
  MetricsData,
  ComparisonData,
  RecommendationData,
  PredictionData
} from '@/types/analytics';

export type VisualizationData = MetricsData | ComparisonData | RecommendationData | PredictionData | Record<string, unknown>[];

export interface Visualization {
  type: 'chart' | 'metric' | 'comparison' | 'recommendation' | 'prediction';
  title: string;
  description?: string;
  data: VisualizationData;
  config?: ChartConfig;
  insights?: string[];
  actions?: string[];
}

interface ResponseGenerationProps {
  visualizations: Visualization[];
  queryType?: QueryType;
  className?: string;
}

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

export function ResponseGeneration({
  visualizations,
  queryType: _queryType,
  className
}: ResponseGenerationProps) {

  const renderChart = (viz: Visualization) => {
    const { type: _type, data, config } = viz;
    
    switch (config?.chartType) {
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data as Record<string, unknown>[]}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={config.xKey || 'name'} />
              <YAxis />
              <Tooltip />
              <Legend />
              {config.lines?.map((line: { key: string; name?: string }, index: number) => (
                <Line 
                  key={line.key}
                  type="monotone" 
                  dataKey={line.key} 
                  stroke={COLORS[index % COLORS.length]}
                  strokeWidth={2}
                  name={line.name || line.key}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        );
      
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data as Record<string, unknown>[]}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={config.xKey || 'name'} />
              <YAxis />
              <Tooltip />
              <Legend />
              {config.bars?.map((bar: { key: string; name?: string }, index: number) => (
                <Bar 
                  key={bar.key}
                  dataKey={bar.key} 
                  fill={COLORS[index % COLORS.length]}
                  name={bar.name || bar.key}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );
      
      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data as Record<string, unknown>[]}
                dataKey={config.valueKey || 'value'}
                nameKey={config.nameKey || 'name'}
                cx="50%"
                cy="50%"
                outerRadius={100}
                fill="#8884d8"
                label={config.showLabels !== false}
              >
                {(data as Record<string, unknown>[]).map((_entry: Record<string, unknown>, index: number) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        );
      
      case 'radar':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <RadarChart data={data as Record<string, unknown>[]}>
              <PolarGrid />
              <PolarAngleAxis dataKey={config.angleKey || 'skill'} />
              <PolarRadiusAxis 
                angle={config.angleOffset || 0}
                domain={config.domain || [0, 100]}
              />
              {config.radars?.map((radar: { key: string; name?: string }, index: number) => (
                <Radar
                  key={radar.key}
                  name={radar.name || radar.key}
                  dataKey={radar.key}
                  stroke={COLORS[index % COLORS.length]}
                  fill={COLORS[index % COLORS.length]}
                  fillOpacity={0.1}
                />
              ))}
              <Tooltip />
              <Legend />
            </RadarChart>
          </ResponsiveContainer>
        );
      
      default:
        return (
          <div className="flex items-center justify-center h-40 text-gray-500">
            Unsupported chart type: {config?.chartType}
          </div>
        );
    }
  };

  const renderMetric = (viz: Visualization) => {
    const data = viz.data as MetricsData;

    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {data.metrics?.map((metric: { label: string; value: number; unit?: string; trend?: number; target?: number; status?: string }, index: number) => (
          <Card key={index} className="p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-gray-600">{metric.label}</h4>
              {metric.trend && (
                <div className={`flex items-center gap-1 text-xs ${
                  metric.trend > 0 ? 'text-green-600' : 
                  metric.trend < 0 ? 'text-red-600' : 'text-gray-600'
                }`}>
                  {metric.trend > 0 ? <TrendingUp className="h-3 w-3" /> : 
                   metric.trend < 0 ? <TrendingDown className="h-3 w-3" /> : null}
                  {Math.abs(metric.trend)}%
                </div>
              )}
            </div>
            
            <div className="text-2xl font-bold text-gray-900">
              {metric.value}
              {metric.unit && <span className="text-sm text-gray-500 ml-1">{metric.unit}</span>}
            </div>
            
            {metric.target && (
              <div className="mt-2">
                <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                  <span>Target: {metric.target}</span>
                  <span>{Math.round((metric.value / metric.target) * 100)}%</span>
                </div>
                <Progress 
                  value={(metric.value / metric.target) * 100} 
                  className="h-1"
                />
              </div>
            )}
            
            {metric.status && (
              <Badge 
                variant={metric.status === 'good' ? 'default' : 
                        metric.status === 'warning' ? 'secondary' : 'destructive'}
                className="mt-2"
              >
                {metric.status}
              </Badge>
            )}
          </Card>
        ))}
      </div>
    );
  };

  const renderComparison = (viz: Visualization) => {
    const data = viz.data as ComparisonData;

    return (
      <div className="space-y-4">
        {data.comparisons?.map((comparison: { title: string; left: string; right: string; leftValue: number | string; rightValue: number | string; metric: string; winner?: string; insights?: string }, index: number) => (
          <Card key={index} className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium">{comparison.title}</h4>
              <Badge variant={comparison.winner === comparison.left ? 'default' : 'secondary'}>
                {comparison.winner} wins
              </Badge>
            </div>
            
            <div className="grid grid-cols-3 gap-4 items-center">
              {/* Left competitor */}
              <div className="text-center">
                <div className="text-lg font-bold text-blue-600">{comparison.left}</div>
                <div className="text-2xl font-bold">{comparison.leftValue}</div>
                <div className="text-xs text-gray-500">{comparison.metric}</div>
              </div>
              
              {/* VS */}
              <div className="text-center text-gray-400 font-medium">VS</div>
              
              {/* Right competitor */}
              <div className="text-center">
                <div className="text-lg font-bold text-green-600">{comparison.right}</div>
                <div className="text-2xl font-bold">{comparison.rightValue}</div>
                <div className="text-xs text-gray-500">{comparison.metric}</div>
              </div>
            </div>
            
            {comparison.insights && (
              <div className="mt-3 pt-3 border-t">
                <p className="text-sm text-gray-600">{comparison.insights}</p>
              </div>
            )}
          </Card>
        ))}
      </div>
    );
  };

  const renderRecommendation = (viz: Visualization) => {
    const data = viz.data as RecommendationData;

    return (
      <div className="space-y-3">
        {data.recommendations?.map((rec: { title: string; description: string; priority: 'low' | 'medium' | 'high'; actions?: string[]; timeframe?: string }, index: number) => (
          <Alert key={index} className={`border-l-4 ${
            rec.priority === 'high' ? 'border-l-red-500' :
            rec.priority === 'medium' ? 'border-l-orange-500' : 'border-l-blue-500'
          }`}>
            <div className="flex items-start gap-3">
              {rec.priority === 'high' ? 
                <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" /> :
                rec.priority === 'medium' ?
                <Target className="h-5 w-5 text-orange-500 mt-0.5" /> :
                <Award className="h-5 w-5 text-blue-500 mt-0.5" />
              }
              
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-medium">{rec.title}</h4>
                  <Badge variant={
                    rec.priority === 'high' ? 'destructive' :
                    rec.priority === 'medium' ? 'secondary' : 'default'
                  }>
                    {rec.priority}
                  </Badge>
                </div>
                
                <AlertDescription className="text-sm">
                  {rec.description}
                </AlertDescription>
                
                {rec.actions && rec.actions.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs font-medium text-gray-600 mb-1">Recommended Actions:</p>
                    <ul className="text-xs text-gray-600 space-y-1">
                      {rec.actions.map((action: string, actionIndex: number) => (
                        <li key={actionIndex} className="flex items-start gap-1">
                          <span className="text-blue-500">•</span>
                          {action}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {rec.timeframe && (
                  <div className="mt-2 text-xs text-gray-500">
                    Expected timeframe: {rec.timeframe}
                  </div>
                )}
              </div>
            </div>
          </Alert>
        ))}
      </div>
    );
  };

  const renderPrediction = (viz: Visualization) => {
    const data = viz.data as PredictionData;

    return (
      <div className="space-y-4">
        {data.predictions?.map((pred: { title: string; current: number | string; predicted: number | string; confidence: number; timeline: string; factors?: string[]; assumptions?: string }, index: number) => (
          <Card key={index} className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium">{pred.title}</h4>
              <Badge variant="outline" className="text-xs">
                {pred.confidence}% confidence
              </Badge>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-xs text-gray-500 mb-1">Current</div>
                <div className="text-xl font-bold text-gray-900">{pred.current}</div>
              </div>
              
              <div className="text-center">
                <div className="text-xs text-gray-500 mb-1">Predicted</div>
                <div className="text-xl font-bold text-blue-600">{pred.predicted}</div>
              </div>
              
              <div className="text-center">
                <div className="text-xs text-gray-500 mb-1">Timeline</div>
                <div className="text-sm font-medium text-gray-700">{pred.timeline}</div>
              </div>
            </div>
            
            {pred.factors && (
              <div className="mt-3 pt-3 border-t">
                <p className="text-xs font-medium text-gray-600 mb-2">Key Factors:</p>
                <div className="flex flex-wrap gap-1">
                  {pred.factors.map((factor: string, factorIndex: number) => (
                    <Badge key={factorIndex} variant="outline" className="text-xs">
                      {factor}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {pred.assumptions && (
              <div className="mt-2">
                <p className="text-xs text-gray-500">{pred.assumptions}</p>
              </div>
            )}
          </Card>
        ))}
      </div>
    );
  };

  const renderVisualization = (viz: Visualization, index: number) => {
    return (
      <Card key={index} className="mb-4">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{viz.title}</CardTitle>
          {viz.description && (
            <p className="text-sm text-gray-600">{viz.description}</p>
          )}
        </CardHeader>
        
        <CardContent>
          {viz.type === 'chart' && renderChart(viz)}
          {viz.type === 'metric' && renderMetric(viz)}
          {viz.type === 'comparison' && renderComparison(viz)}
          {viz.type === 'recommendation' && renderRecommendation(viz)}
          {viz.type === 'prediction' && renderPrediction(viz)}
          
          {/* Insights */}
          {viz.insights && viz.insights.length > 0 && (
            <div className="mt-4 pt-4 border-t">
              <h5 className="text-sm font-medium text-gray-700 mb-2">Key Insights:</h5>
              <ul className="text-sm text-gray-600 space-y-1">
                {viz.insights.map((insight, insightIndex) => (
                  <li key={insightIndex} className="flex items-start gap-2">
                    <span className="text-blue-500 mt-1">•</span>
                    {insight}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Actions */}
          {viz.actions && viz.actions.length > 0 && (
            <div className="mt-3">
              <h5 className="text-sm font-medium text-gray-700 mb-2">Suggested Actions:</h5>
              <div className="flex flex-wrap gap-2">
                {viz.actions.map((action, actionIndex) => (
                  <Badge key={actionIndex} variant="outline" className="text-xs">
                    {action}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  if (!visualizations || visualizations.length === 0) {
    return null;
  }

  return (
    <div className={className}>
      {visualizations.map((viz, index) => renderVisualization(viz, index))}
    </div>
  );
}